import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Badge,
  Button,
  Toast,
  ToastBody,
} from "reactstrap";
import PublishModal from "./PublishModal";
import ViewScheduleModal from "./ViewScheduleModal";
import CampaignService from "../../../services/OrderingServices/CampaignService";

const CampaignInfoCard = ({ campaign, devices = [], onPublish, triggerPublishModal }) => {
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("success");
  const lastTriggeredRef = useRef(0);
  const hasAutoOpenedRef = useRef(false); // Track if modal was ever auto-opened

  // Listen for trigger to open publish modal
  useEffect(() => {
    // Don't open modal if:
    // - Already auto-opened before (prevents reopening on refetch)
    // - Campaign is already published
    // - This trigger was already processed
    if (
      triggerPublishModal > 0 && 
      triggerPublishModal !== lastTriggeredRef.current && 
      !showPublishModal &&
      !hasAutoOpenedRef.current && // NEW: Never reopen if already opened once
      campaign?.isDraft !== false
    ) {
      setShowPublishModal(true);
      lastTriggeredRef.current = triggerPublishModal;
      hasAutoOpenedRef.current = true; // Mark as opened
    }
  }, [triggerPublishModal, showPublishModal, campaign?.isDraft]);

  // Force close modal if campaign becomes published
  useEffect(() => {
    if (showPublishModal && campaign?.isDraft === false) {
      setShowPublishModal(false);
    }
  }, [campaign?.isDraft, showPublishModal]);

  if (!campaign) return null;

  // const getCampaignStatus = () => {
  //   if (campaign.isDraft) {
  //     return { label: "Draft", color: "secondary" };
  //   }

  //   const now = new Date();
  //   const start = campaign.startDate ? new Date(campaign.startDate) : null;
  //   const end = campaign.endDate ? new Date(campaign.endDate) : null;

  //   if (start && now < start) {
  //     return { label: "Scheduled", color: "warning" };
  //   }

  //   if (start && end && now >= start && now <= end) {
  //     return { label: "Live", color: "success" };
  //   }

  //   if (end && now > end) {
  //     return { label: "Expired", color: "dark" };
  //   }

  //   return { label: "Inactive", color: "danger" };
  // };

  const getCampaignStatus = () => {
    if (campaign.isDraft) {
      return { label: "Draft", color: "secondary" };
    }

    const now = new Date();
    const start = campaign.startDate ? new Date(campaign.startDate) : null;
    const end = campaign.endDate ? new Date(campaign.endDate) : null;

    // Not started yet
    if (start && now < start) {
      return { label: "Scheduled", color: "warning" };
    }

    // Started and no end date
    if (start && !end && now >= start) {
      return { label: "Live (Ongoing)", color: "success" };
    }

    // Normal live window
    if (start && end && now >= start && now <= end) {
      return { label: "Live", color: "success" };
    }

    // Expired
    if (end && now > end) {
      return { label: "Expired", color: "dark" };
    }

    return { label: "Inactive", color: "danger" };
  };
  const campaignStatus = getCampaignStatus();

  const handlePublishNow = async (updatedCampaign) => {
    try {
      setToastMessage("Campaign published successfully!");
      setToastColor("success");
      setTimeout(() => setToastMessage(""), 3000);

      if (onPublish) {
        await onPublish(updatedCampaign);
      }
    } catch (error) {
      setToastMessage("Failed to publish campaign");
      setToastColor("danger");
      setTimeout(() => setToastMessage(""), 3000);
    }
  };

  const handleSchedule = async (updatedCampaign) => {
    try {
      setToastMessage("Campaign scheduled successfully!");
      setToastColor("success");
      setTimeout(() => setToastMessage(""), 3000);

      if (onPublish) {
        await onPublish(updatedCampaign);
      }
    } catch (error) {
      setToastMessage("Failed to schedule campaign");
      setToastColor("danger");
      setTimeout(() => setToastMessage(""), 3000);
    }
  };

  const handleUnpublish = async () => {
    try {
      await CampaignService.updateCampaignStatus(campaign.id, true);
      setToastMessage("Campaign unpublished successfully!");
      setToastColor("success");
      setTimeout(() => setToastMessage(""), 3000);

      if (onPublish) {
        await onPublish({ ...campaign, isDraft: true });
      }
    } catch (error) {
      setToastMessage("Failed to unpublish campaign");
      setToastColor("danger");
      setTimeout(() => setToastMessage(""), 3000);
    }
  };

  return (
    <>
      <Card className="border-0 shadow-sm mb-3">
        <CardHeader className="bg-light border-bottom d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Campaign Information</h5>

          <div className="d-flex align-content-center gap-2">
            <Button
              color="primary"
              size="sm"
              data-publish-button-main
              onClick={() => setShowPublishModal(true)}
              disabled={campaign.isDraft === false}
              title={
                campaign.isDraft === false
                  ? "Campaign is already published"
                  : "Click to publish campaign"
              }
            >
              <i className="mdi mdi-publish me-1"></i>
              Publish
            </Button>

            <Button
              color="warning"
              size="sm"
              outline
              onClick={handleUnpublish}
              disabled={campaign.isDraft === true}
              title={
                campaign.isDraft === true
                  ? "Campaign is already unpublished"
                  : "Click to unpublish campaign"
              }
            >
              <i className="mdi mdi-cancel me-1"></i>
              Unpublish
            </Button>

            <Button
              color="info"
              size="sm"
              outline
              onClick={() => setShowScheduleModal(true)}
              title="View campaign schedule details"
            >
              <i className="mdi mdi-calendar-clock me-1"></i>
              View Schedule
            </Button>
          </div>
        </CardHeader>

        <CardBody className="py-3">
          <Row className="g-2">
            <Col md="2">
              <small className="text-muted fw-semibold">Campaign Name</small>
              <div className="small">{campaign.name}</div>
            </Col>

            <Col md="2">
              <small className="text-muted fw-semibold">Associated Ads</small>
              <div className="small">{campaign.adsCount || 0}</div>
            </Col>

            <Col md="2">
              <small className="text-muted fw-semibold">Start Date</small>
              <div className="small">
                {campaign.startDate
                  ? new Date(campaign.startDate).toLocaleString()
                  : "—"}
              </div>
            </Col>

            <Col md="2">
              <small className="text-muted fw-semibold">End Date</small>
              <div className="small">
                {campaign.endDate
                  ? new Date(campaign.endDate).toLocaleString()
                  : "—"}
              </div>
            </Col>

            <Col md="2">
              <small className="text-muted fw-semibold">Status</small>
              <div className="small">
                <Badge color={campaignStatus.color}>
                  {campaignStatus.label}
                </Badge>
              </div>
            </Col>

            <Col md="2">
              <small className="text-muted fw-semibold">Visibility</small>
              <div className="small">
                <Badge color={campaign.isDraft ? "secondary" : "primary"}>
                  {campaign.isDraft ? "Draft" : "Published"}
                </Badge>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        campaign={{ ...campaign, devices }}
        onPublishNow={handlePublishNow}
        onSchedule={handleSchedule}
      />

      <ViewScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        campaign={campaign}
        onScheduleUpdate={async (scheduleId, payload) => {
          try {
            // You can add API call here to update the schedule
            // For now, just show success message and refresh if onPublish is provided
            setToastMessage("Schedule updated successfully!");
            setToastColor("success");
            setTimeout(() => setToastMessage(""), 3000);
            
            if (onPublish) {
              await onPublish(campaign);
            }
          } catch (error) {
            setToastMessage("Failed to update schedule");
            setToastColor("danger");
            setTimeout(() => setToastMessage(""), 3000);
          }
        }}
      />

      {toastMessage && (
        <Toast
          className="position-fixed bottom-0 end-0 m-3"
          style={{ zIndex: 9999 }}
        >
          <ToastBody color={toastColor} className="text-white">
            {toastMessage}
          </ToastBody>
        </Toast>
      )}
    </>
  );
};

export default CampaignInfoCard;
